import React from 'react';
import { BaseModal, Button } from '@airclou/ui';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TenantErasurePreview } from '../../../services/api';
import { ErasureStoreList } from './ErasureStoreList';

interface OrganizationDeletedModalProps {
  isOpen: boolean;
  /** ISO timestamp after which the data is erased. */
  purgeAfter: string;
  /** What the erasure will remove; null when the server could not draw it. */
  erasurePreview: TenantErasurePreview | null;
  /**
   * The only way out. The organization is already gone, so every path off this
   * screen — the button, the backdrop, Escape, the close icon — signs out.
   */
  onSignOut: () => void;
}

export const OrganizationDeletedModal: React.FC<OrganizationDeletedModalProps> = ({
  isOpen,
  purgeAfter,
  erasurePreview,
  onSignOut,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language || undefined;
  const formatCount = (n: number) => new Intl.NumberFormat(locale).format(n);
  const purgeDate = new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(purgeAfter));

  const accounts = erasurePreview?.accounts ?? null;
  const you = accounts?.you ?? null;
  const remainsIn = you?.outcome === 'kept' ? (you.remainsIn ?? []) : [];

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onSignOut}
      title={
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <span>{t('deleteOrg.resultTitle')}</span>
        </div>
      }
      size="md"
      footer={
        <div className="flex items-center justify-end">
          <Button onClick={onSignOut}>{t('deleteOrg.signOut')}</Button>
        </div>
      }
      contentClassName="p-6"
    >
      <div className="space-y-5 text-sm">
        <div className="space-y-1 text-gray-700 dark:text-gray-300">
          <p>{t('deleteOrg.scheduledFor', { date: purgeDate })}</p>
          <p className="text-gray-600 dark:text-gray-400">{t('deleteOrg.effectRestore')}</p>
        </div>

        {erasurePreview ? (
          <>
            <section>
              <h3 className="font-medium text-foreground mb-2">{t('deleteOrg.whatWillBeErased')}</h3>
              <ErasureStoreList preview={erasurePreview} />
            </section>

            <section className="space-y-1">
              <h3 className="font-medium text-foreground mb-2">{t('deleteOrg.accountsTitle')}</h3>
              {/* Say "we could not work this out" rather than "0 accounts will
                  be deleted" — the second is a claim, and a failed query is not
                  evidence for it. */}
              {accounts === null ? (
                <p className="text-gray-500 dark:text-gray-400">{t('deleteOrg.accountsUnavailable')}</p>
              ) : (
                <>
                  {/* Two sentences, not one: i18next pluralises on a single `count`,
                      and "1 member accounts" is exactly the sort of thing a person
                      reads on the worst day of their relationship with us. */}
                  <p className="text-gray-700 dark:text-gray-300">
                    {t('deleteOrg.accountsDeleted', {
                      count: accounts.toDelete,
                      n: formatCount(accounts.toDelete),
                    })}{' '}
                    {t('deleteOrg.accountsKept', {
                      count: accounts.toKeep,
                      n: formatCount(accounts.toKeep),
                    })}
                  </p>
                  {you?.outcome === 'deleted' && (
                    <p className="text-gray-700 dark:text-gray-300">{t('deleteOrg.yourAccountDeleted')}</p>
                  )}
                  {you?.outcome === 'kept' && (
                    <p className="text-gray-700 dark:text-gray-300">
                      {remainsIn.length > 0
                        ? t('deleteOrg.yourAccountKept', { organizations: remainsIn.map((o) => o.name).join(', ') })
                        : t('deleteOrg.yourAccountKeptElsewhere')}
                    </p>
                  )}
                </>
              )}
            </section>
          </>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">{t('deleteOrg.previewUnavailable')}</p>
        )}
      </div>
    </BaseModal>
  );
};
